import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Collapse, Pagination, Alert } from 'react-bootstrap';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import {
    CampaignResultStatusBadge,
    CampaignStatusBadge,
} from './campaignDetailUtils';
import { useCampaign } from './useCampaign';
import { getShortId, toProperCase } from '../process/processQueueUtils';
function CampaignStats() {
    const { campaignId } = useParams();
    const { campaign, isLoading: campaignLoading, error: campaignError, refetch } = useCampaign(campaignId);

    const [itemsPerPage, setItemsPerPage] = useState(30);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [resultItems, setResultItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        sent: 0,
        read: 0,
        failed: 0,
        unsubscribed: 0,
    });
    const [statsLoading, setStatsLoading] = useState(false);

    const isSms = campaign?.type === 'SMS';
    const receiverLabel = isSms ? 'Phone' : 'Email';

    const fetchStats = useCallback(() => {
        if (!campaignId) return;

        setStatsLoading(true);
        campaignService
            .getCampaignResultStats(campaignId)
            .then((res) => {
                setStats({
                    total: res.data.total ?? 0,
                    sent: res.data.sent ?? 0,
                    read: res.data.read ?? 0,
                    failed: res.data.failed ?? 0,
                    unsubscribed: res.data.unsubscribed ?? 0,
                });
            })
            .catch(() => {
                setStats({
                    total: 0,
                    sent: 0,
                    read: 0,
                    failed: 0,
                    unsubscribed: 0,
                });
            })
            .finally(() => setStatsLoading(false));
    }, [campaignId]);

    const fetchResults = useCallback(() => {
        if (!campaignId) return;

        setIsLoading(true);
        setError(null);

        campaignService
            .getCampaignResult(campaignId, currentPage, itemsPerPage)
            .then((res) => {
                setResultItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setExpandedRowId(null);
            })
            .catch((err) => {
                setResultItems([]);
                if (err?.response?.status === 403) {
                    setError('You do not have permission to view campaign results.');
                } else {
                    setError('Failed to load campaign results. Please try again.');
                }
            })
            .finally(() => setIsLoading(false));
    }, [campaignId, currentPage, itemsPerPage]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleRefresh = () => {
        refetch();
        fetchStats();
        fetchResults();
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = resultItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    return (
        <div className="container-fluid px-3 px-lg-4">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h1 className="mb-0">
                    <i className="bi bi-bar-chart me-2"></i>
                    Campaign Statistics
                </h1>
                <div className="d-flex align-items-center gap-2">
                    <Link to="/campaign" className="campaign-queue-page__back d-inline-flex align-items-center gap-1">
                        <i className="bi bi-arrow-left"></i>
                        Back to campaigns
                    </Link>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={campaignLoading || isLoading || statsLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </Button>
                </div>
            </div>

            {campaignLoading ? (
                <Loading />
            ) : campaignError ? (
                <Alert variant="warning">{campaignError}</Alert>
            ) : (
                campaign && (
                    <>
                        <div className="campaign-queue-context p-3 p-md-4 mb-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div className="flex-grow-1">
                                    <div className="text-muted small mb-1">{campaign.clinicName || '—'}</div>
                                    <div className="campaign-queue-context__title fw-semibold mb-1">
                                        {campaign.name || 'Untitled campaign'}
                                    </div>
                                    {campaign.title && (
                                        <div className="campaign-queue-context__meta text-muted mb-2">
                                            {campaign.title}
                                        </div>
                                    )}
                                    <div className="d-flex flex-wrap gap-2">
                                        <span
                                            className={`badge campaign-queue-type-badge ${
                                                isSms
                                                    ? 'bg-success bg-opacity-10 text-success'
                                                    : 'bg-primary bg-opacity-10 text-primary'
                                            }`}
                                        >
                                            <i
                                                className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-1`}
                                            ></i>
                                            {campaign.type}
                                        </span>
                                        <span className="badge bg-light text-dark border campaign-queue-type-badge">
                                            {toProperCase(campaign.category)}
                                        </span>
                                        <CampaignStatusBadge
                                            status={campaign.status}
                                            statusCode={campaign.statusCode}
                                        />
                                    </div>
                                </div>
                                <div className="text-md-end">
                                    <div className="text-muted small mb-1">Campaign ID</div>
                                    <div className="font-monospace small text-break">{campaign.id}</div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-6 col-lg">
                                <div className="campaign-queue-stat campaign-queue-stat--total">
                                    <div className="campaign-queue-stat__label">Total results</div>
                                    <div className="campaign-queue-stat__value">{stats.total}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg">
                                <div className="campaign-queue-stat campaign-queue-stat--sent">
                                    <div className="campaign-queue-stat__label">Sent</div>
                                    <div className="campaign-queue-stat__value">{stats.sent}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg">
                                <div className="campaign-queue-stat campaign-queue-stat--read">
                                    <div className="campaign-queue-stat__label">Read</div>
                                    <div className="campaign-queue-stat__value">{stats.read}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg">
                                <div className="campaign-queue-stat campaign-queue-stat--failed">
                                    <div className="campaign-queue-stat__label">Failed</div>
                                    <div className="campaign-queue-stat__value">{stats.failed}</div>
                                </div>
                            </div>
                            <div className="col-6 col-lg">
                                <div className="campaign-queue-stat campaign-queue-stat--unsubscribed">
                                    <div className="campaign-queue-stat__label">Unsubscribed</div>
                                    <div className="campaign-queue-stat__value">{stats.unsubscribed}</div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 campaign-queue-table-card">
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center flex-wrap gap-1 mb-3">
                                    <i className="bi bi-table text-primary me-1"></i>
                                    <h5 className="card-title mb-0" style={{ color: '#012970' }}>
                                        Campaign Results
                                        <span
                                            className="text-muted fw-normal"
                                            style={{ fontSize: '0.9rem', marginLeft: '0.35rem' }}
                                        >
                                            ({totalElements} total)
                                        </span>
                                    </h5>
                                </div>

                                {isLoading ? (
                                    <Loading />
                                ) : error && resultItems.length === 0 ? (
                                    <Alert variant="warning">{error}</Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Patient</th>
                                                    <th>{receiverLabel}</th>
                                                    <th>Status</th>
                                                    <th>Created</th>
                                                    <th style={{ width: '56px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resultItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="text-center text-muted py-5">
                                                            <i className="bi bi-inbox d-block fs-3 mb-2"></i>
                                                            No campaign results found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    resultItems.map((item) => (
                                                        <React.Fragment key={item.id}>
                                                            <tr
                                                                className={
                                                                    expandedRowId === item.id ? 'table-active' : ''
                                                                }
                                                            >
                                                                <td>
                                                                    <div className="fw-medium">
                                                                        {item.patientName || '—'}
                                                                    </div>
                                                                    <small
                                                                        className="font-monospace text-muted"
                                                                        title={item.patientId}
                                                                    >
                                                                        {getShortId(item.patientId)}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small className="campaign-queue-receiver">
                                                                        {item.receiver || '—'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <CampaignResultStatusBadge status={item.status} />
                                                                </td>
                                                                <td>
                                                                    <small>{item.createdAt || '—'}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant={
                                                                            expandedRowId === item.id
                                                                                ? 'outline-secondary'
                                                                                : 'outline-primary'
                                                                        }
                                                                        size="sm"
                                                                        onClick={() => toggleRow(item.id)}
                                                                        title={
                                                                            expandedRowId === item.id
                                                                                ? 'Collapse'
                                                                                : 'Expand'
                                                                        }
                                                                        aria-expanded={expandedRowId === item.id}
                                                                    >
                                                                        <i
                                                                            className={`bi bi-chevron-${
                                                                                expandedRowId === item.id
                                                                                    ? 'up'
                                                                                    : 'down'
                                                                            }`}
                                                                        ></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                            <tr className="p-0 border-top-0">
                                                                <td colSpan={5} className="p-0 border-top-0">
                                                                    <Collapse in={expandedRowId === item.id}>
                                                                        <div className="p-3 bg-light border-top">
                                                                            <div className="row">
                                                                                <DetailField label="Result ID" mono>
                                                                                    {item.id}
                                                                                </DetailField>
                                                                                <DetailField label="Campaign ID" mono>
                                                                                    {item.campaignId}
                                                                                </DetailField>
                                                                                <DetailField label="Patient ID" mono>
                                                                                    {item.patientId}
                                                                                </DetailField>
                                                                                <DetailField label="Clinic ID" mono>
                                                                                    {item.clinicId}
                                                                                </DetailField>
                                                                                <DetailField label="Type">
                                                                                    {item.type || '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Status Code">
                                                                                    {item.statusCode ?? '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Modified">
                                                                                    {item.modifiedAt || '—'}
                                                                                </DetailField>
                                                                            </div>
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {resultItems.length > 0 && (
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3 pt-3 border-top">
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <span className="text-muted small">
                                                Showing {startItem}–{endItem} of {totalElements}
                                            </span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                <option value={10}>10 per page</option>
                                                <option value={30}>30 per page</option>
                                                <option value={50}>50 per page</option>
                                                <option value={100}>100 per page</option>
                                            </select>
                                        </div>
                                        {paginationControls}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
}

export default CampaignStats;
